/**
 * Printing a report through the CK Doctor desktop service instead of the
 * browser.
 *
 * The browser's own print pipeline is what makes printing slow, and a counter
 * usually has more than one printer — a sticker printer next to a document
 * printer — which the browser cannot choose between. The desktop service can.
 *
 * It is a printer and nothing more: it answers who the machine is and what it
 * can print on, and it prints a page it is handed. It does not know what a
 * report is, so which printer a report belongs to is decided here, by whoever
 * knows the report. Nothing in this module asks the machine to fetch anything
 * — it hands over bytes the browser already holds — so a page printed this way
 * cannot become a request the service makes on someone else's behalf.
 *
 * What this module does NOT do is render the PDF. The page renders it exactly
 * as it does today and hands over the bytes it already holds. The desktop
 * refuses to download from anywhere but the backend's own origin, so a blob
 * url from the browser is no use to it — the page sends the bytes instead.
 *
 * Every call here goes to the machine the operator is sitting at, and that is
 * enforced rather than assumed: the `url` argument each export takes is there
 * for tests and for a service on a different local port, and a host that is not
 * this machine is refused before anything is sent. Use `ensureDesktopService`
 * from `desktopService.util` first: nothing in here starts the desktop app.
 */
import {
  DESKTOP_SERVICE_URL,
  normalizeDesktopUrl,
} from 'utility/desktopService.util'

/**
 * How long one call to the machine may take.
 *
 * Generous next to the 2 s health probe, because this carries a PDF. Without it
 * a request the machine never answers leaves the caller waiting for ever with
 * no error and no way out but a reload.
 */
export const DESKTOP_REQUEST_TIMEOUT_MS = 30000

/**
 * A page bigger than this is refused before it is sent.
 *
 * The same 12 MB the service refuses at on both of its print routes, not a
 * margin under anything — refusing here only turns its refusal into a sentence
 * in the operator's own language. Raising it re-opens the split where a page
 * printed or not depending on which route it took.
 */
export const MAX_PRINT_BYTES = 12 * 1024 * 1024

/** The range of copies the desktop service accepts; outside it the job is refused there. */
const MIN_PRINT_COPIES = 1
const MAX_PRINT_COPIES = 100
const PRINT_PAPER_SIZES = ['auto', 'A4', 'Letter', 'A5', '80mm', '58mm']

/** The one job status that means the page reached a printer, as the service names it. */
const PRINTED_STATUS = 'printed'

/** The only hosts this module will talk to: the machine it is running on. */
const LOOPBACK_HOSTS = ['localhost', '127.0.0.1', '[::1]']
/**
 * And only over http. A scheme the browser does not know has no origin, so
 * `parsed.origin` is the string "null" and the address built from it is a
 * *relative* url — resolved against the page, which is the backend. The one
 * that makes this worth checking is `ckdoctor://`, a constant in this module's
 * own dependency: it names this machine and would still send the page away.
 */
const ALLOWED_PROTOCOLS = ['http:', 'https:']

/**
 * The base url to use, refusing anything that is not this machine.
 *
 * A patient's report leaves the browser as bytes in this request. The `url`
 * argument exists so a test can point somewhere else and so a site can move
 * the service's port, not so a page can be talked into posting a PDF to
 * someone else's server.
 */
const localDesktopUrl = (url = DESKTOP_SERVICE_URL) => {
  const normalized = normalizeDesktopUrl(url)
  let parsed
  try {
    parsed = new URL(normalized)
  } catch (e) {
    throw new Error(`ที่อยู่ของโปรแกรมไม่ถูกต้อง: ${normalized}`)
  }
  if (
    !ALLOWED_PROTOCOLS.includes(parsed.protocol) ||
    !LOOPBACK_HOSTS.includes(parsed.hostname)
  ) {
    throw new Error('โปรแกรมพิมพ์ต้องอยู่บนเครื่องนี้เท่านั้น')
  }
  // origin + path only: a '#' or '?' left on the url would otherwise swallow
  // the path appended after it and every call would land on '/'
  return `${parsed.origin}${parsed.pathname.replace(/\/+$/, '')}`
}

const requestDesktop = async (path, options = {}, url = DESKTOP_SERVICE_URL) => {
  const base = localDesktopUrl(url)
  const controller =
    typeof AbortController === 'undefined' ? null : new AbortController()
  const timer = controller
    ? setTimeout(() => controller.abort(), DESKTOP_REQUEST_TIMEOUT_MS)
    : null
  try {
    const response = await fetch(`${base}${path}`, {
      ...options,
      // after the caller's options, not before: a signal passed in would
      // otherwise replace the budget and the request could hang for ever
      ...(controller ? { signal: controller.signal } : {}),
    })
    // The body is read inside the budget too. `fetch` resolves on the headers,
    // so disarming the timer here would leave a reply that starts and never
    // finishes hanging for ever — which is the case the timeout exists for.
    //
    // The catch below tolerates a body that is not JSON. It must not tolerate
    // the abort as well: swallowed, a timed-out read comes back as an empty
    // body and an ok response, and the caller is told the machine has no
    // printers rather than that it never answered.
    const body = await response.json().catch((err) => {
      if (err?.name === 'AbortError') throw err
      return null
    })
    if (!response.ok) {
      const message =
        (body && (body.message || body.error)) || `HTTP ${response.status}`
      throw new Error(Array.isArray(message) ? message.join(', ') : message)
    }
    return body
  } catch (err) {
    if (err?.name === 'AbortError') {
      throw new Error('โปรแกรมพิมพ์บนเครื่องนี้ไม่ตอบกลับ')
    }
    throw err
  } finally {
    if (timer) clearTimeout(timer)
  }
}

/**
 * Who this machine is and what it can print on, in one call.
 *
 * `machineName` is the hostname, so it is there whether or not the machine was
 * ever registered with a backend — registering is optional, and a machine that
 * skipped it prints the same.
 *
 * Mapped in full so the endpoint's contract is pinned here rather than read
 * back from the service, even though the button reads only `printers`. Note
 * that `defaultPrinterName` is a backend setting an operator chooses and is
 * usually empty; the machine's own default is the `isDefault` row of the list.
 */
// Retain the full response for the diagnostic test page (os, version, IP and
// future Desktop fields), while normal report callers keep the normalized API.
export const getDesktopDeviceResponse = (url) =>
  requestDesktop('/printers/device', { method: 'GET', cache: 'no-store' }, url)

export const getDesktopDevice = async (url) => {
  const body = await getDesktopDeviceResponse(url)
  const device = body?.data ?? body ?? {}
  return {
    machineName: typeof device.machineName === 'string' ? device.machineName : '',
    desktopDeviceId: device.desktopDeviceId ?? null,
    registered: device.registered === true,
    printers: Array.isArray(device.printers) ? device.printers : [],
    printerSource: device.printerSource || 'none',
    defaultPrinterName: device.defaultPrinterName || '',
  }
}

const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    const giveUp = (cause) => {
      const error = new Error('อ่านไฟล์ PDF ไม่สำเร็จ')
      // keep what the browser said for the console; the operator reads the
      // Thai sentence above it
      error.cause = cause
      reject(error)
    }
    reader.onerror = () => giveUp(reader.error)
    // nothing calls abort() today, but a promise that never settles leaves the
    // button spinning forever, which is the one outcome with no way out
    reader.onabort = () => giveUp(new Error('aborted'))
    reader.onload = () => {
      const result = String(reader.result || '')
      if (!result) {
        giveUp(new Error('FileReader returned nothing'))
        return
      }
      // a data url is "data:application/pdf;base64,<payload>"
      const comma = result.indexOf(',')
      resolve(comma === -1 ? result : result.slice(comma + 1))
    }
    reader.readAsDataURL(blob)
  })

/**
 * Send a page this browser already holds to a printer on this machine.
 *
 * The caller names the printer — by `refId` where it has one, because two
 * printers can share the name they show and only the operating system's own id
 * tells them apart.
 */
export const printReportOnDesktop = async (
  { printerName, refId, blob, copies, paperSize },
  url,
) => {
  if (!blob) throw new Error('ยังไม่มีไฟล์ PDF ให้พิมพ์')
  // An ArrayBuffer has no `.size`, so `undefined > MAX` is false and it would
  // slip past the cap, then fail deep in FileReader with an English DOM
  // message that a caller puts straight on a Thai screen.
  if (typeof Blob === 'undefined' || !(blob instanceof Blob)) {
    throw new Error('ไฟล์ที่ส่งมาไม่ใช่ไฟล์ PDF')
  }
  // a render that answered 200 with nothing in it reaches here as a 0-byte
  // blob, and an empty page prints as an empty page
  if (blob.size === 0) throw new Error('ไฟล์ PDF ที่ได้มาว่างเปล่า')
  if (blob.size > MAX_PRINT_BYTES) {
    throw new Error('ไฟล์ใหญ่เกินกว่าจะส่งให้โปรแกรมพิมพ์ได้')
  }
  if (copies !== undefined) {
    if (!Number.isInteger(copies)) {
      throw new Error('จำนวนชุดที่พิมพ์ต้องเป็นจำนวนเต็ม')
    }
    // the same range the service accepts; without this a 0 was dropped and
    // printed one copy, and a -1 went out on the wire as -1
    if (copies < MIN_PRINT_COPIES || copies > MAX_PRINT_COPIES) {
      throw new Error(
        `จำนวนชุดที่พิมพ์ต้องอยู่ระหว่าง ${MIN_PRINT_COPIES} ถึง ${MAX_PRINT_COPIES}`,
      )
    }
  }
  if (paperSize !== undefined && !PRINT_PAPER_SIZES.includes(paperSize)) {
    throw new Error('ขนาดกระดาษไม่รองรับ: ' + String(paperSize))
  }
  // One identifier goes on the wire, and the page is what decides which.
  // Sending both and letting the machine pick would move that decision across
  // the wire, and an id of nothing but spaces would win it.
  const chosenRefId = String(refId ?? '').trim()
  const chosenName = String(printerName ?? '').trim()
  const chosen = chosenRefId
    ? { refId: chosenRefId }
    : chosenName
      ? { printerName: chosenName }
      : null
  if (!chosen) throw new Error('ยังไม่ได้เลือกเครื่องพิมพ์')
  // refuse a host that is not this machine before the page is even read
  localDesktopUrl(url)

  const fileBase64 = await blobToBase64(blob)
  const body = await requestDesktop(
    '/printers/print',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        printJobId: `his-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        ...chosen,
        fileBase64,
        ...(copies ? { copies } : {}),
        ...(paperSize !== undefined ? { paperSize } : {}),
      }),
    },
    url,
  )
  const result = body?.data ?? body
  if (!result || result.status !== PRINTED_STATUS) {
    throw new Error(result?.message || 'พิมพ์ไม่สำเร็จ')
  }
  return result
}
